import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";

type BrandDestination = {
  label: string;
  path: string;
  opensWorkArea?: boolean;
};

type BrandDestinationGroup = {
  label: "Brand settings" | "Brand work";
  destinations: BrandDestination[];
};

function destinationGroups(brandId: string): BrandDestinationGroup[] {
  const basePath = `/brand/${encodeURIComponent(brandId)}`;
  return [
    {
      label: "Brand settings",
      destinations: [
        { label: "Profile", path: `${basePath}/settings/profile` },
        { label: "Voice", path: `${basePath}/settings/voice`, opensWorkArea: true },
        { label: "Context docs", path: `${basePath}/settings/context-docs` },
        { label: "Surfaces", path: `${basePath}/settings/surfaces` },
        { label: "Corrections", path: `${basePath}/settings/corrections` },
        { label: "Visuals", path: `${basePath}/settings/visuals` },
      ],
    },
    {
      label: "Brand work",
      // These routes own their specialist workspace shells, so signal the context change here.
      destinations: [
        { label: "Documents", path: `${basePath}/docs`, opensWorkArea: true },
        { label: "Assets", path: `${basePath}/assets`, opensWorkArea: true },
        { label: "Research", path: `${basePath}/research`, opensWorkArea: true },
        { label: "Campaigns", path: `${basePath}/campaigns`, opensWorkArea: true },
      ],
    },
  ];
}

export function BrandSettingsNav() {
  const { brandId } = useParams<{ brandId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  if (!brandId) return null;

  const groups = destinationGroups(brandId);
  const destinations = groups.flatMap((group) => group.destinations);
  const selectedPath = destinations.find(({ path }) => (
    location.pathname === path || location.pathname.startsWith(`${path}/`)
  ))?.path ?? groups[0]!.destinations[0]!.path;

  return (
    <>
      <div className="mb-4 md:hidden">
        <label htmlFor="brand-section" className="mb-1.5 block text-xs font-semibold text-slate-700">
          Brand section
        </label>
        <select
          id="brand-section"
          aria-label="Brand section"
          value={selectedPath}
          onChange={(event) => navigate(event.target.value)}
          className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          {groups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.destinations.map((destination) => (
                <option key={destination.path} value={destination.path}>
                  {destination.label}{destination.opensWorkArea ? " — work area" : ""}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <nav aria-label="Brand sections" className="hidden rounded-[20px] border border-slate-200 bg-white p-3 shadow-sm md:block">
        {groups.map((group, index) => (
          <section key={group.label} className={index ? "mt-3 border-t border-slate-200 pt-3" : ""}>
            <h2 className="px-3 py-2 font-data text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
              {group.label}
            </h2>
            <div className="space-y-1">
              {group.destinations.map((destination) => (
                <NavLink
                  key={destination.path}
                  to={destination.path}
                  aria-label={destination.opensWorkArea
                    ? `${destination.label} — opens brand work area`
                    : undefined}
                  className={({ isActive }) =>
                    `flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-brand-50 text-brand-800"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`
                  }
                >
                  <span>{destination.label}</span>
                  {destination.opensWorkArea ? (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                      Work area
                    </span>
                  ) : null}
                </NavLink>
              ))}
            </div>
          </section>
        ))}
      </nav>
    </>
  );
}
